import { useCallback, useEffect, useRef, useState } from "react"
import { track } from '@vercel/analytics'
import useGame from "../../stores/useGame"
import SEC_Window from '../SEC_Window.jsx'
import POI_Window from '../POI_Window.jsx'
import { loadSECData } from '../../utils/secDataLoader.js'
import { loadPOIData } from '../../utils/poiDataLoader.js'
import '../../style.css'

// Must stay in sync with SEC_Markers.jsx and SEC_Window.jsx
const SEC_SUBSECTIONS = {
    SEC006: { parent: 'SEC001' }
}

// Reverse map: parent SEC → array of child sub-section SEC IDs
// e.g. { SEC001: ['SEC006'] }
const SEC_CHILDREN = Object.entries(SEC_SUBSECTIONS).reduce((acc, [child, { parent }]) => {
    if (!acc[parent]) acc[parent] = []
    acc[parent].push(child)
    return acc
}, {})

const WINDOW_EXIT_DURATION_MS = 600

export default function UI() {
    const viewState = useGame(s => s.viewState)
    const exitToDefault = useGame(s => s.exitToDefault)
    const cinematicMode = useGame(s => s.cinematicMode)
    const setOpenPoiIndex = useGame(s => s.setOpenPoiIndex)
    const setSelectedPoiData = useGame(s => s.setSelectedPoiData)
    const selectedSec = useGame(s => s.selectedSec)
    const releaseWeaponFaded = useGame(s => s.releaseWeaponFaded)
    const setReleaseWeaponFaded = useGame(s => s.setReleaseWeaponFaded)

    const [currentSecData, setCurrentSecData] = useState(null)
    const [currentSecPOIs, setCurrentSecPOIs] = useState([])
    const [currentPoiData, setCurrentPoiData] = useState(null)
    const [closingPopups, setClosingPopups] = useState([])

    // Refs so stable event handlers can read live state without stale closures
    const currentSecDataRef = useRef(null)
    const currentSecPOIsRef = useRef([])
    useEffect(() => { currentSecDataRef.current = currentSecData }, [currentSecData])
    useEffect(() => { currentSecPOIsRef.current = currentSecPOIs }, [currentSecPOIs])

    // Load SEC data and POIs when selected SEC changes
    useEffect(() => {
        let closeTimer1, closeTimer2

        if (!selectedSec) {
            if (currentSecData) {
                setClosingPopups(prev => [...prev, { type: 'sec', secData: currentSecData, pois: currentSecPOIs, direction: 'bottom' }])
                closeTimer1 = setTimeout(() => {
                    setCurrentSecData(null)
                    setCurrentSecPOIs([])
                    setClosingPopups(prev => prev.slice(1))
                }, WINDOW_EXIT_DURATION_MS)
            } else {
                setCurrentSecData(null)
                setCurrentSecPOIs([])
            }
            return () => { clearTimeout(closeTimer1) }
        }

        Promise.all([loadSECData(), loadPOIData()]).then(([secMap, poiMap]) => {
            const secData = secMap[selectedSec]
            const poisForSec = poiMap
                ? Object.values(poiMap).filter(poi => poi.secRef === selectedSec)
                : []

            if (currentSecData && currentSecData.id !== secData.id) {
                setClosingPopups(prev => [...prev, { type: 'sec', secData: currentSecData, pois: currentSecPOIs, direction: 'bottom' }])
                closeTimer2 = setTimeout(() => {
                    setClosingPopups(prev => prev.slice(1))
                }, WINDOW_EXIT_DURATION_MS)
            }

            if (currentPoiData) setCurrentPoiData(null)

            // Subsections skip the SEC_Window — jump straight to combined POI_Window.
            // isPlaceholder: true means "show prompt text only, no product list" —
            // the actual per-product detail is surfaced when a 3D marker is clicked.
            if (SEC_SUBSECTIONS[secData.id]) {
                const products = poisForSec.flatMap(poi => poi.products || [])
                setCurrentSecData(secData)
                setCurrentSecPOIs(poisForSec)
                setCurrentPoiData({ category: secData.section, products, poiName: poisForSec[0]?.name || null, isPlaceholder: true })
                track('poi_opened_from_subsection', { section: secData.section })
                return
            }

            // For parent SECs, also include POIs from child sub-sections so they
            // appear as additional category buttons in SEC_Window (e.g. "Weapon Stores"
            // under "Weapons Systems").
            const childSecIds = SEC_CHILDREN[secData.id] || []
            const childPOIs = childSecIds.flatMap(childId =>
                Object.values(poiMap).filter(poi => poi.secRef === childId)
            )

            setCurrentSecData(secData)
            setCurrentSecPOIs([...poisForSec, ...childPOIs])
        })

        return () => { clearTimeout(closeTimer1); clearTimeout(closeTimer2) }
    }, [selectedSec])

    // Open POI window from SEC popup button click
    const openPoiByCategory = useCallback(async (category) => {
        if (!category || !currentSecPOIsRef.current?.length) return

        const isSubSection = currentSecDataRef.current && SEC_SUBSECTIONS[currentSecDataRef.current.id]

        // Check if the clicked category belongs to a child sub-section of the current SEC
        // (e.g. "Weapon Stores" clicked inside Weapons Systems)
        const childSecIds = currentSecDataRef.current ? (SEC_CHILDREN[currentSecDataRef.current.id] || []) : []
        const matchingChildPOIs = currentSecPOIsRef.current.filter(p => p.category === category && childSecIds.includes(p.secRef))
        const isChildSubCategory = !isSubSection && matchingChildPOIs.length > 0

        let matchingPOIs, displayCategory
        if (isSubSection) {
            matchingPOIs = currentSecPOIsRef.current
            displayCategory = currentSecDataRef.current.section
        } else {
            matchingPOIs = currentSecPOIsRef.current.filter(p => p.category === category)
            displayCategory = category
        }

        const products = matchingPOIs.flatMap(poi => poi.products || [])
        const poiName = matchingPOIs.length > 0 ? matchingPOIs[0].name : null

        // If this category belongs to a child sub-section, replicate exactly what
        // clicking that SEC's 3D hotspot does: move the camera to its position.
        // We do NOT dispatch sec-selected here — the bomb bay is already open from
        // the parent SEC (SEC001), and dispatching sec-selected for SEC006 would
        // cause WeaponsSystemAnim to re-evaluate bay state unnecessarily.
        if (isChildSubCategory) {
            const childSecId = matchingChildPOIs[0].secRef
            const secMap = await loadSECData()
            const childSecData = secMap[childSecId]
            if (childSecData) {
                useGame.getState().enterSecZoom({
                    secId: childSecData.id,
                    posId: childSecData.position || null,
                    tarId: childSecData.target || null,
                })
                // Move the camera to POS/TAR without dispatching sec-selected
                // (which would cause WeaponsSystemAnim to re-evaluate bay state)
                window.dispatchEvent(new CustomEvent('sec-camera-move', {
                    detail: { secData: childSecData }
                }))
            }
        }

        if (poiName && !isSubSection && !isChildSubCategory) {
            window.dispatchEvent(new CustomEvent('poi-selected', {
                detail: { poiName, fromWindow: true }
            }))
        }

        if (currentSecDataRef.current) {
            setClosingPopups(prev => [...prev, { type: 'sec', secData: currentSecDataRef.current, pois: currentSecPOIsRef.current, direction: 'bottom' }])
            setTimeout(() => setClosingPopups(prev => prev.slice(1)), WINDOW_EXIT_DURATION_MS)
        }

        setCurrentPoiData({ category: displayCategory, products, poiName })
        track('poi_opened_from_sec_popup', { category: displayCategory, section: currentSecDataRef.current?.section })
    }, [])

    // Listen for 3D POI marker selection events
    useEffect(() => {
        const handler = async (event) => {
            const poiName = event.detail?.poiName
            if (!poiName) return
            const poiMap = await loadPOIData()
            if (!poiMap) return
            const poiData = poiMap[poiName]
            if (!poiData) return
            setCurrentPoiData({ category: poiData.category, products: poiData.products || [], poiName, displayTitle: poiData.type === 'info' ? poiData.products?.[0]?.name : poiData.category })
            track('poi_opened_from_3d', { poi: poiName, category: poiData.category })
        }
        window.addEventListener('poi-selected', handler)
        return () => window.removeEventListener('poi-selected', handler)
    }, [])

    // Handle BACK from subsection POI_Window → return to parent SEC
    useEffect(() => {
        const handler = async (event) => {
            const { fromSec, returnToSec } = event.detail || {}
            if (!returnToSec) return

            const [secMap, poiMap] = await Promise.all([loadSECData(), loadPOIData()])
            const parentSecData = secMap[returnToSec]
            const poisForParent = poiMap
                ? Object.values(poiMap).filter(poi => poi.secRef === returnToSec)
                : []

            useGame.getState().enterSecZoom({
                secId: returnToSec,
                posId: parentSecData?.position || null,
                tarId: parentSecData?.target || null,
            })

            // Reset the subsection marker so it can be clicked again
            window.dispatchEvent(new CustomEvent('sec-deselected', {
                detail: { secName: fromSec, toSecName: returnToSec }
            }))
            window.dispatchEvent(new CustomEvent('sec-selected', {
                detail: { secName: returnToSec, secData: parentSecData }
            }))

            if (currentSecDataRef.current) {
                setClosingPopups(prev => [...prev, {
                    type: 'sec',
                    secData: currentSecDataRef.current,
                    pois: currentSecPOIsRef.current,
                    direction: 'bottom'
                }])
                setTimeout(() => setClosingPopups(prev => prev.slice(1)), WINDOW_EXIT_DURATION_MS)
            }

            setCurrentSecData(parentSecData)
            setCurrentSecPOIs(poisForParent)
            setCurrentPoiData(null)
        }

        window.addEventListener('sec-subsection-back', handler)
        return () => window.removeEventListener('sec-subsection-back', handler)
    }, [])

    // Handle BACK from a POI_Window to a subsection SEC view
    useEffect(() => {
        const handler = async (event) => {
            const { returnToSec } = event.detail || {}
            const [secMap, poiMap] = await Promise.all([loadSECData(), loadPOIData()])
            const targetSecData = secMap[returnToSec]
            const poisForSec = poiMap
                ? Object.values(poiMap).filter(poi => poi.secRef === returnToSec)
                : []

            useGame.getState().enterSecZoom({
                secId: returnToSec,
                posId: targetSecData?.position || null,
                tarId: targetSecData?.target || null,
            })

            setCurrentSecData(targetSecData)
            setCurrentSecPOIs(poisForSec)
            setCurrentPoiData(null)
        }

        window.addEventListener('poi-back-to-subsection', handler)
        return () => window.removeEventListener('poi-back-to-subsection', handler)
    }, [])

    // Initial fade-in for RELEASE WEAPON button
    useEffect(() => {
        if (releaseWeaponFaded) return
        const timer = setTimeout(() => setReleaseWeaponFaded(true), 1000)
        return () => clearTimeout(timer)
    }, [releaseWeaponFaded, setReleaseWeaponFaded])

    return <>
        <div className='banner-top'>
            <img
                src="/graphics/the-lee-co-full-white.svg"
                className="image-wrap"
                onClick={() => {
                    window.open('https://www.theleeco.com/')
                    track('Lee_Logo_Clicked')
                }}
            />
            <div className='banner-divider' />
            <div className='lowerTitle'>
                <div className='redHead'>DEFENSE</div>
                <div className='heading'>Next Generation Fighter</div>
                <div className='subheading'>Select a point of interest</div>
            </div>
        </div>

        {viewState === 'default_state' && !cinematicMode && (
            <div
                className={`release-weapon-button ${!releaseWeaponFaded ? 'initial-fade' : ''}`}
                onClick={() => {
                    track('release_weapon_clicked')
                    window.dispatchEvent(new CustomEvent('cinematic-mode'))
                }}
                title="Release Weapon - Cinematic View"
            >
                <div className="release-weapon-wrapper">
                    <span className="release-weapon-label">RELEASE WEAPON</span>
                    <div className="release-weapon-arrow" />
                </div>
            </div>
        )}

        {viewState !== 'default_state' && !cinematicMode && (
            <div
                className="aircraft-view-button"
                onClick={() => {
                    track('aircraft_view_clicked')
                    setCurrentPoiData(null)
                    setCurrentSecData(null)
                    setCurrentSecPOIs([])
                    window.dispatchEvent(new CustomEvent('sec-deselected', {
                        detail: { secName: selectedSec, toSecName: null }
                    }))
                    exitToDefault()
                }}
                title="Return to Aircraft View"
            >
                <div className="aircraft-view-wrapper">
                    <span className="aircraft-view-label">AIRCRAFT VIEW</span>
                    <div className="aircraft-view-arrow" />
                </div>
            </div>
        )}

        {closingPopups.map((popup, idx) => (
            popup.type === 'poi' ? (
                <POI_Window
                    key={`closing-poi-${idx}`}
                    secData={popup.secData}
                    poiData={popup.poiData}
                    isClosing={true}
                    closeDirection={popup.direction}
                    onClose={() => {}}
                />
            ) : (
                <SEC_Window
                    key={`closing-${popup.secData.id}-${idx}`}
                    secData={popup.secData}
                    isClosing={true}
                    closeDirection={popup.direction}
                    onClose={() => {}}
                />
            )
        ))}

        {currentSecData && !currentPoiData && (
            <SEC_Window
                key={`current-${currentSecData.id}`}
                secData={currentSecData}
                isClosing={viewState !== 'sec_zoomed'}
                closeDirection="bottom"
                onClose={() => {
                    if (SEC_SUBSECTIONS[currentSecData.id]) return
                    window.dispatchEvent(new CustomEvent('sec-deselected', {
                        detail: { secName: selectedSec, toSecName: null }
                    }))
                    exitToDefault()
                }}
            />
        )}

        {currentPoiData && (
                <POI_Window
                    key={`poi-${currentPoiData.category}-${currentPoiData.isPlaceholder ? 'placeholder' : currentPoiData.poiName}`}
                secData={currentSecData}
                poiData={currentPoiData}
                isClosing={false}
                onClose={() => {
                    window.dispatchEvent(new CustomEvent('poi-deselected', {
                        detail: { poiName: currentPoiData.poiName }
                    }))
                    setOpenPoiIndex(null)
                    setSelectedPoiData(null)

                    const closingSnapshot = currentPoiData
                    setClosingPopups(prev => [...prev, { type: 'poi', secData: currentSecData, poiData: closingSnapshot, direction: 'bottom' }])
                    setCurrentPoiData(null)
                    setTimeout(() => setClosingPopups(prev => prev.slice(1)), 1000)

                    if (currentSecData && SEC_SUBSECTIONS[currentSecData.id]) {
                        if (closingSnapshot.isPlaceholder) {
                            // Closing the placeholder itself — go back to parent section
                            setCurrentSecData(null)
                            setCurrentSecPOIs([])
                            window.dispatchEvent(new CustomEvent('sec-subsection-back', {
                                detail: {
                                    fromSec: currentSecData.id,
                                    returnToSec: SEC_SUBSECTIONS[currentSecData.id].parent
                                }
                            }))
                        } else {
                            // Closing a product window — restore the Weapon Stores placeholder
                            const secPOIs = currentSecPOIsRef.current
                            const products = secPOIs.flatMap(poi => poi.products || [])
                            setCurrentPoiData({
                                category: currentSecData.section,
                                products,
                                poiName: secPOIs[0]?.name || null,
                                isPlaceholder: true
                            })
                        }
                    }
                }}
            />
        )}
    </>
}
